import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { Input } from '../../components/ui/input';
import {
  MagnifyingGlass,
  TestTube,
  Scroll,
  Dna,
  Virus,
  Database,
  Package,
  Funnel
} from '@phosphor-icons/react';

const entityIcons = {
  experiments: TestTube,
  protocols: Scroll,
  plasmids: Dna,
  strains: Virus,
  samples: Database,
  inventory: Package,
  sequences: Dna
};

const entityColors = {
  experiments: 'bg-blue-100 text-indigo-400',
  protocols: 'bg-green-100 text-green-400',
  plasmids: 'bg-purple-100 text-purple-700',
  strains: 'bg-orange-100 text-orange-700',
  samples: 'bg-cyan-100 text-cyan-700',
  inventory: 'bg-red-100 text-red-400',
  sequences: 'bg-indigo-900/40 text-indigo-300'
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entityFilter, setEntityFilter] = useState('');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      handleSearch(q);
    }
  }, [searchParams]);

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const data = await api.search(searchQuery, entityFilter || null);
      setResults(data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
      handleSearch(query);
    }
  };

  const getTotalResults = () => {
    if (!results) return 0;
    return Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  };

  const getEntityLink = (entity, item) => {
    const routes = {
      experiments: `/experiments/${item.id}`,
      protocols: `/protocols/${item.id}`,
      plasmids: `/plasmids/${item.id}`,
      strains: `/strains/${item.id}`,
      samples: `/samples/${item.id}`,
      inventory: `/inventory/${item.id}`,
      sequences: `/sequences`
    };
    return routes[entity] || '#';
  };

  return (
    <div data-testid="search-page">
      <div className="mb-6">
        <h1 className="heading-display">Search</h1>
        <p className="text-zinc-500 mt-1">Search across all lab data</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSubmit} className="card-flat p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search experiments, plasmids, strains, protocols..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 input-field w-full"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-zinc-400" />
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="input-field"
              data-testid="entity-filter"
            >
              <option value="">All Types</option>
              <option value="experiments">Experiments</option>
              <option value="protocols">Protocols</option>
              <option value="plasmids">Plasmids</option>
              <option value="strains">Strains</option>
              <option value="samples">Samples</option>
              <option value="inventory">Inventory</option>
              <option value="sequences">Sequences</option>
            </select>
          </div>
          <button type="submit" className="btn-primary px-6" data-testid="search-btn">
            Search
          </button>
        </div>
      </form>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="spinner" />
        </div>
      ) : results ? (
        <div>
          <p className="text-sm text-zinc-500 mb-4" data-testid="results-count">
            Found {getTotalResults()} result{getTotalResults() !== 1 ? 's' : ''} for "{searchParams.get('q')}"
          </p>

          {getTotalResults() === 0 ? (
            <div className="card-flat p-8 text-center">
              <MagnifyingGlass size={48} className="text-zinc-300 mx-auto mb-4" />
              <h3 className="heading-card text-slate-100 mb-2">No results found</h3>
              <p className="text-sm text-zinc-500">Try different keywords or filters</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(results).map(([entity, items]) => {
                if (items.length === 0) return null;
                const Icon = entityIcons[entity] || Database;
                const colorClass = entityColors[entity] || 'bg-zinc-100 text-zinc-700';

                return (
                  <div key={entity} className="card-flat overflow-hidden" data-testid={`results-${entity}`}>
                    <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2">
                      <Icon size={18} />
                      <h3 className="font-medium capitalize">{entity}</h3>
                      <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-sm">{items.length}</span>
                    </div>
                    <div className="divide-y divide-zinc-100">
                      {items.map((item) => (
                        <Link
                          key={item.id}
                          to={getEntityLink(entity, item)}
                          className="search-result-item block"
                        >
                          <div className="flex items-start gap-3">
                            <span className={`search-result-type ${colorClass}`}>{entity.slice(0, -1)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-100 truncate">
                                {item.title || item.name}
                              </p>
                              <p className="text-sm text-zinc-500 truncate">
                                {item.description || item.hypothesis || item.organism || item.category || '-'}
                              </p>
                            </div>
                            <span className="text-xs text-zinc-400">
                              {new Date(item.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="card-flat p-8 text-center">
          <MagnifyingGlass size={48} className="text-zinc-300 mx-auto mb-4" />
          <h3 className="heading-card text-slate-100 mb-2">Search your lab data</h3>
          <p className="text-sm text-zinc-500">Enter a query to search across experiments, plasmids, strains, and more</p>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
